// src/lib/sentry-client.js
// Client-side Sentry init for the React app. Lives in src/lib/ so it's inside
// the tsconfig strict `include` scope — new code is strict even though src/main.jsx
// (the consumer) is not. See CLAUDE.md gotcha 52 and plan 2026-04-18-parity-gaps §13.
//
// Init is gated on VITE_SENTRY_DSN — missing DSN = silent no-op (intentional for local dev).

import * as Sentry from '@sentry/react'

/** @type {boolean} */
let initialized = false

/** @type {RegExp[]} — must match the patterns in api/lib/sentry.js SECRET_PATTERNS. */
const SECRET_PATTERNS = [
  /sk_live_[A-Za-z0-9_-]+/g,
  /sk_test_[A-Za-z0-9_-]+/g,
  /whsec_[A-Za-z0-9_-]+/g,
  /sk-ant-api03-[A-Za-z0-9_-]+/g,
  /sbp_[A-Za-z0-9_-]+/g,
  /sntrys_[A-Za-z0-9_\-=.]+/g,
  /sntryu_[A-Za-z0-9_\-=.]+/g,
  /vcp_[A-Za-z0-9_-]+/g,
  /vca_[A-Za-z0-9_-]+/g,
  /re_[A-Za-z0-9_-]+/g,
  /eyJ[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+\.[A-Za-z0-9_\-]+/g,
]

/**
 * @param {unknown} value
 * @returns {unknown}
 */
function scrubValue(value) {
  if (typeof value === 'string') {
    let out = value
    for (const pattern of SECRET_PATTERNS) {
      out = out.replace(pattern, '[REDACTED]')
    }
    return out
  }
  if (Array.isArray(value)) return value.map(scrubValue)
  if (value && typeof value === 'object') {
    /** @type {Record<string, unknown>} */
    const out = {}
    for (const [k, v] of Object.entries(value)) {
      out[k] = scrubValue(v)
    }
    return out
  }
  return value
}

/**
 * Idempotent client Sentry init. Called once from src/main.jsx before ReactDOM.createRoot.
 * @returns {void}
 */
export function initSentryClient() {
  if (initialized) return
  const dsn = import.meta.env.VITE_SENTRY_DSN
  if (!dsn) {
    initialized = true
    return
  }

  Sentry.init({
    dsn,
    environment: import.meta.env.MODE === 'production' ? 'production' : 'development',
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
    integrations: [
      Sentry.browserTracingIntegration(),
    ],
    /**
     * @param {import('@sentry/react').ErrorEvent} event
     * @returns {import('@sentry/react').ErrorEvent | null}
     */
    beforeSend(event) {
      if (event.request) {
        event.request = /** @type {import('@sentry/react').ErrorEvent['request']} */ (scrubValue(event.request))
      }
      if (event.message) {
        event.message = /** @type {string} */ (scrubValue(event.message))
      }
      if (event.exception?.values) {
        event.exception.values = event.exception.values.map((ex) => ({
          ...ex,
          value: ex.value ? /** @type {string} */ (scrubValue(ex.value)) : ex.value,
        }))
      }
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((bc) => /** @type {import('@sentry/react').Breadcrumb} */ (scrubValue(bc)))
      }
      return event
    },
    initialScope: {
      tags: {
        runtime: 'browser',
      },
    },
  })
  initialized = true
}

/**
 * Client-side exception capture. Pairs with captureApiException on the server.
 * Safe to call when Sentry is disabled — falls through to console.error.
 * @param {unknown} err
 * @param {{component?: string, action?: string, accountId?: string, userId?: string, extra?: Record<string, unknown>}} [context]
 * @returns {void}
 */
export function captureClientException(err, context = {}) {
  // eslint-disable-next-line no-console
  console.error(err)
  if (!import.meta.env.VITE_SENTRY_DSN) return

  try {
    Sentry.withScope((scope) => {
      if (context.component) scope.setTag('component', context.component)
      if (context.action) scope.setTag('action', context.action)
      if (context.accountId) scope.setTag('account_id', context.accountId)
      if (context.userId) scope.setUser({ id: context.userId })
      if (context.extra) scope.setContext('extra', /** @type {Record<string, unknown>} */ (scrubValue(context.extra)))
      Sentry.captureException(err)
    })
  } catch (sentryErr) {
    // eslint-disable-next-line no-console
    console.error('[sentry-client.capture] self-failure:', sentryErr)
  }
}

// Re-export the Sentry ErrorBoundary primitive so consumers don't depend on @sentry/react directly.
// This keeps the Sentry dep centralized in src/lib/.
export { ErrorBoundary as SentryErrorBoundary } from '@sentry/react'
